import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ShoppingCart, Menu, X, PhoneCall, Bike, ChevronRight, Zap } from 'lucide-react';
import { useCart } from '../../context/CartContext';
import { CONTACT_INFO, categories } from '../../data/mock';
import Logo from '../Logo';

const Header = () => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const { cartCount, openCart } = useCart();
  const location = useLocation();

  useEffect(() => { setIsMobileMenuOpen(false); }, [location]);

  const phoneHref = `tel:${CONTACT_INFO.phone.replace(/[^0-9]/g, '')}`;

  const navLinks = [
    { name: 'Home', path: '/' },
    { name: 'Shop', path: '/shop' },
    { name: 'Our Story', path: '/about' },
    { name: 'Help', path: '/faq' },
    { name: 'Contact', path: '/contact' },
  ];

  return (
    <header className="sticky top-0 left-0 right-0 z-50">
      {/* Announcement bar */}
      <div className="bg-athletic-600 text-white text-xs sm:text-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-9 flex items-center justify-center gap-2 text-center">
          <Zap className="w-3.5 h-3.5 text-power-300" />
          <p className="font-medium tracking-wide">Free USPS shipping on every order &middot; 2&ndash;4 day US delivery</p>
        </div>
      </div>

      {/* Main row */}
      <div className="bg-white border-b border-dark-100 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-[72px] gap-4">
            <Logo />

            <nav className="hidden lg:flex items-center gap-8">
              {navLinks.map((link) => {
                const isActive = location.pathname === link.path;
                return (
                  <Link
                    key={link.path}
                    to={link.path}
                    className={`relative text-[15px] font-semibold transition-colors py-1 ${isActive ? 'text-athletic-600' : 'text-dark-700 hover:text-athletic-600'}`}
                  >
                    {link.name}
                    {isActive && <span className="absolute -bottom-0.5 left-0 right-0 h-0.5 bg-athletic-600 rounded-full" />}
                  </Link>
                );
              })}
            </nav>

            <div className="flex items-center gap-2 sm:gap-3">
              <a href={phoneHref} className="hidden xl:flex items-center gap-2 text-sm font-semibold text-dark-700 hover:text-athletic-600 transition-colors">
                <PhoneCall className="w-4 h-4 text-athletic-600" /> {CONTACT_INFO.phone}
              </a>
              <button
                onClick={openCart}
                data-testid="cart-button"
                className="relative w-11 h-11 rounded-full flex items-center justify-center text-white bg-athletic-600 hover:bg-athletic-700 transition-colors shadow-button"
                aria-label="Cart"
              >
                <ShoppingCart className="w-5 h-5" />
                {cartCount > 0 && (
                  <span className="absolute -top-1 -right-1 w-5 h-5 bg-power-500 text-dark-900 text-[10px] font-extrabold rounded-full flex items-center justify-center border-2 border-white">
                    {cartCount}
                  </span>
                )}
              </button>
              <button
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                className="lg:hidden w-11 h-11 rounded-full flex items-center justify-center text-dark-900 bg-dark-100 hover:bg-dark-200"
                aria-label="Menu"
              >
                {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
              </button>
            </div>
          </div>
        </div>

        {/* Persistent category bar */}
        <div className="hidden lg:block bg-dark-50 border-t border-dark-100">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center gap-1 h-11">
              <Link to="/shop" className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[13px] font-bold text-athletic-700 bg-athletic-100 hover:bg-athletic-200 transition-colors">
                <Bike className="w-3.5 h-3.5" /> All Gear
              </Link>
              {categories.map((cat) => (
                <Link
                  key={cat.id}
                  to={`/shop?category=${cat.slug}`}
                  className="px-3 py-1.5 rounded-full text-[13px] font-medium text-dark-600 hover:text-athletic-700 hover:bg-white transition-colors whitespace-nowrap"
                >
                  {cat.name}
                </Link>
              ))}
            </div>
          </div>
        </div>

        {/* Mobile menu */}
        {isMobileMenuOpen && (
          <div className="lg:hidden border-t border-dark-100 bg-white">
            <div className="max-w-7xl mx-auto px-4 py-3 space-y-1">
              {navLinks.map((link) => (
                <Link key={link.path} to={link.path} className="flex items-center justify-between px-4 py-3 text-base font-semibold text-dark-700 hover:bg-athletic-50 hover:text-athletic-600 rounded-xl">
                  {link.name}<ChevronRight className="w-4 h-4 text-dark-300" />
                </Link>
              ))}
              <div className="pt-3 border-t border-dark-100 mt-3">
                <p className="px-4 text-xs uppercase tracking-[0.2em] text-dark-400 mb-2 flex items-center gap-2"><Bike className="w-3.5 h-3.5 text-athletic-600" /> Categories</p>
                {categories.map((cat) => (
                  <Link key={cat.id} to={`/shop?category=${cat.slug}`} className="block px-4 py-2.5 text-sm text-dark-600 hover:bg-athletic-50 hover:text-athletic-600 rounded-xl">{cat.name}</Link>
                ))}
              </div>
              <a href={phoneHref} className="flex items-center gap-2 mt-3 px-4 py-3 rounded-full bg-athletic-600 text-white font-semibold justify-center">
                <PhoneCall className="w-4 h-4" /> Call {CONTACT_INFO.phone}
              </a>
            </div>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;
